/* eslint-disable jsx-a11y/control-has-associated-label */
import { format } from 'd3-format';
import { Link } from 'found';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import Pagination from 'src/enosikit/components/Pagination/components/Pagination';
import Time from 'src/enosikit/components/Time';
import { buildPropertyReference } from 'src/helpers/tradeHelpers';
import {
  PLATFORM_MODE_REBATE, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
} from 'src/util/constants';
import { convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { unitOfMeasure } from 'src/util/i18n/energyPrice';
import { getLocale, i18nDecimalFormat } from 'src/util/i18n/handler';
import getMeterLabel from 'src/util/meter';

import TradeListTablePlaceholder from './Placeholder';
import TradesSummary from './TradesSummary';

const IntraDayPagination = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
`;

const InterDayPagination = styled.ul`
  justify-content: space-between;
  display: flex;
  flex-direction: row;
`;

const PaginationTableCell = styled.td`
  padding: var(--bs-card-cap-padding-y, 1rem) var(--bs-card-cap-padding-x, 1rem) !important;
  background-color: var(--bs-card-cap-bg, rgba(0, 0, 0, 0.03)) !important;
  border-top: var(--bs-card-border-width, 1px) solid var(--bs-card-border-color, #dee2e6);
`;

/**
 * Returns the Trader/Trade Point React element
 * @param {import('react-intl').IntlShape} intl - i18n react-intl
 * @param {object} trader - user data object
 * @returns {React.ReactElement} - trader details (meter, property etc)
 */
export const getTrader = (intl, trader) => {
  if (!trader?.tradePoint) {
    return null;
  }
  const {
    communityId, community, tradePoint, userId, user,
  } = trader;
  const { uuid, type, meter } = tradePoint || {};
  const { identifier, title } = meter || {};
  const meterLabel = getMeterLabel(identifier, title);
  const propertyReference = buildPropertyReference(tradePoint, false);

  const copyUserIdTitle = intl.formatMessage({
    id: 'trade.trade_list.trade_list_table.copy_user_id_title',
    defaultMessage: 'Copy user id to the clipboard',
  });
  const copyUserIdSuccessMsg = intl.formatMessage({
    id: 'trade.trade_list.trade_list_table.copy_user_id.success.message',
    defaultMessage: 'User id copied to the clipboard.',
  });
  const copyCommunityIdTitle = intl.formatMessage({
    id: 'trade.trade_list.trade_list_table.copy_community_id_title',
    defaultMessage: 'Copy community id to the clipboard',
  });
  const copyCommunityIdSuccessMsg = intl.formatMessage({
    id: 'trade.trade_list.trade_list_table.copy_community_id.success.message',
    defaultMessage: 'Community id copied to the clipboard',
  });
  const copyTradePointIdTitle = intl.formatMessage({
    id: 'trade.trade_list.trade_list_table.copy_trade_point_id_title',
    defaultMessage: 'Copy trade point id to the clipboard',
  });
  const copyTradePointIdSuccessMsg = intl.formatMessage({
    id: 'trade.trade_list.trade_list_table.copy_trade_point_id.success.message',
    defaultMessage: 'Trade point id copied to the clipboard',
  });

  return (
    <dl>
      {user && (
        <>
          <dt><FormattedMessage id="trade.trade_list.trade_list_table.user.trader.label" defaultMessage="Trader" /></dt>
          <dd>
            {displayUserName(intl, user)}
            <br />
            <UUID
              copyTitle={copyUserIdTitle}
              copySuccessMsg={copyUserIdSuccessMsg}
              uuid={userId}
            />
          </dd>
        </>
      )}
      {community && (
        <>
          <dt><FormattedMessage id="trade.trade_list.trade_list_table.community.trader.label" defaultMessage="Trader" /></dt>
          <dd>
            {community.title}
            <br />
            <UUID
              copyTitle={copyCommunityIdTitle}
              copySuccessMsg={copyCommunityIdSuccessMsg}
              uuid={communityId}
            />
          </dd>
        </>
      )}
      <dt><FormattedMessage id="trade.trade_list.trade_list_table.trade_point.label" defaultMessage="Trade Point" /></dt>
      <dd>
        {meterLabel && (
          <>
            <span>{meterLabel}</span>
            <br />
          </>
        )}
        {propertyReference && (
          <>
            {propertyReference}
            <br />
          </>
        )}
        <UUID
          copyTitle={copyTradePointIdTitle}
          copySuccessMsg={copyTradePointIdSuccessMsg}
          uuid={uuid}
        />
        <br />
        <BadgeTradePointType type={type} />
      </dd>
    </dl>
  );
};

const locale = getLocale();

/**
 * Generates a trade summary component for a given trade object and internationalization context.
 * @param {object} trade - The trade object to generate the summary for.
 * @returns {React.ReactElement} - A React element representing the trade summary.
 */
const buildTradeSummary = (trade) => {
  if (!trade) {
    return null;
  }
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  // TO DO: Implement d3-format locale support (PT-1124)
  const formattedTradeVolume = i18nDecimalFormat(format('.4s')(trade.volume || 0));
  const tradeVolumeUnitTitle = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.trade_volume.abbr.title', defaultMessage: 'watt hour' });
  const tradeVolumeUnitLabel = <FormattedMessage id="trade.trade_list.trade_list_table.trade_volume.abbr.label" defaultMessage="Wh" />;
  const tradeVolumeUnit = <abbr title={tradeVolumeUnitTitle}>{tradeVolumeUnitLabel}</abbr>;
  const energyCostUnit = unitOfMeasure(intl);
  const modeRebateDiscountLabel = APIConfig().MODE === PLATFORM_MODE_REBATE && <FormattedMessage id="trade.trade_list.trade_list_table.discount_of.label" defaultMessage="Discount of " />;

  // TO DO: Implement d3-format locale support (PT-1124)
  const formattedTradeValue = intlCurrency.formatMessage(
    {
      id: 'common.currency.value',
      defaultMessage: '{n, number, :: .00 currency/AUD}',
    },
    { n: (trade?.value || 0) },

  );
  // TO DO: Implement d3-format locale support (PT-1124)
  const formattedConvertEnergyPrice = (
    <FormattedMessage
      id="common.currency.extended_value"
      description="Energy trading requires extended precision beyond standard 2-decimal currency formatting for accurate price display in trade lists"
      defaultMessage="{n, number, :: .000#}"
      values={{
        n: (convertEnergyPricePowertracerToPartner(intlCurrency, trade.price))?.toNumber(),
      }}
    />
  );

  return (
    <>
      <FormattedMessage
        id="trade.trade_list.trade_list_table.trade_summary.trade_volume.text"
        defaultMessage="{tradeVolumeAndAbbrLabel} @ {convertEnergyPricePowertracerToPartner} {energyCostUnit}"
        values={{
          /* eslint-disable react/jsx-one-expression-per-line */
          tradeVolumeAndAbbrLabel: <strong>{formattedTradeVolume}{tradeVolumeUnit}</strong>,
          convertEnergyPricePowertracerToPartner: <strong>{formattedConvertEnergyPrice}</strong>,
          energyCostUnit,
        }}
      />
      <br />
      {APIConfig().MODE === PLATFORM_MODE_REBATE
        && (
          <FormattedMessage
            id="trade.trade_list.trade_list_table.trade_summary.trade_value.text"
            defaultMessage="{modeRebateDiscountLabel} {tradeValue}"
            values={{
              modeRebateDiscountLabel,
              tradeValue: <strong>{formattedTradeValue}</strong>,
            }}
          />
        )}
    </>
  );
};

/**
 * Displays a paginated table of trades with daily summaries and navigation controls.
 * @param {object} props
 * @param {DateTime} props.date - Selected date for trade display
 * @param {Function} props.dateUpdateFunc - Updates the selected date
 * @param {Function} props.handlePageChange - Changes the current page in pagination
 * @param {boolean} props.dateOutOfRange - Indicates whether the selected date is outside
 * the property's active range
 * @param {string} [props.noDataMessage] - Message to display when the user cancelled
 * a request and there is no current data available. Overrides the default "No trades
 * available" message.
 * @param {boolean} props.overlayVisible - Controls loading overlay visibility
 * @param {object} props.loadingOverlayProps - Configuration for loading overlay component
 * @param {object} props.paginationInfo - Pagination state including current page and total items
 * @param {string} props.propertyId - Property UUID for trade rule navigation links
 * @param {string} props.timezone - Timezone for timestamp formatting
 * @param {Array<object>} props.trades - Array of Trade objects for display and summary calculations
 * @returns {React.ReactElement} TradeListTable component
 */
function TradeListTable({
  date, dateUpdateFunc, handlePageChange = undefined, dateOutOfRange, noDataMessage = null,
  overlayVisible, loadingOverlayProps, paginationInfo, propertyId, timezone, trades,
}) {
  const intl = useIntl();

  const {
    paginatedItems: paginatedTrades, pageCount, itemCount,
    pageStartIndex, pageEndIndex, currentPage,
  } = paginationInfo || {};

  const setDate = (event, dateSelected) => {
    event.preventDefault();
    dateUpdateFunc(dateSelected);
  };

  const after = date.plus({ days: 1 });
  const before = date.plus({ days: -1 });

  const copyTradeRuleIdTitle = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.copy_title', defaultMessage: 'Copy trade rule id to the clipboard' });
  const copyTradeRuleIdSuccessMsg = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.copy.success.message', defaultMessage: 'Trade rule id copied to the clipboard.' });
  const noTradesAvailableMessage = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.no_trades.message', defaultMessage: 'No trades available' });

  return (
    <>
      {overlayVisible && (
        <>
          <LoadingOverlay inputs={loadingOverlayProps} />
          <TradeListTablePlaceholder />
        </>
      )}

      {!overlayVisible && (
      <div className="mt-4 mb-4 card trades-history-list">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.id.label', defaultMessage: 'ID' })}</th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.timestamp.label', defaultMessage: 'Timestamp' })} </th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.details.label', defaultMessage: 'Details' })}</th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.direction.label', defaultMessage: 'Direction' })} </th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.type.label', defaultMessage: 'Type' })} </th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.tradepoint.label', defaultMessage: 'Trade Point' })}  </th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.counterparty.label', defaultMessage: 'Counterparty' })}</th>
                <th scope="col">{intl.formatMessage({ id: 'trade.trade_list.trade_list_table.head.rule.label', defaultMessage: 'Rule' })} </th>
              </tr>
            </thead>
            <tbody>
              {(!paginatedTrades || paginatedTrades?.length === 0 || dateOutOfRange) && (
              <tr>
                <td colSpan={8} className="alert">
                  <div className="alert alert-warning mb-0">
                    {noDataMessage || noTradesAvailableMessage}
                  </div>
                </td>
              </tr>
              )}
              {paginatedTrades && !dateOutOfRange && paginatedTrades.map((trade) => {
                const {
                  uuid, direction, interval, meter, type, ruleId, rule,
                } = trade || {};
                const { buyer, seller } = rule || {};
                const trader = direction === TRADE_DIRECTION_BUY ? buyer : seller;
                const tradePoint = trader?.tradePoint;
                const propertyReference = buildPropertyReference(tradePoint, false);
                const intervalLength = interval?.length
                  ? `(${interval.length / 60}${intl.formatMessage({ id: 'minute_short', defaultMessage: 'min' })})`
                  : '';
                return (
                  <tr key={`trade-${uuid}`}>
                    <th scope="row" colSpan={2}>
                      <UUID uuid={uuid} />
                      <div data-testid="timestamp" style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                        <FormattedMessage
                          id="trade.trade_list.trade_list_table.cell.timestamp.date.text"
                          defaultMessage="{formattedTimeDate}"
                          values={{
                            formattedTimeDate: <Time
                              child={DateTime.fromSeconds(interval.timestamp, { zone: timezone }).setLocale(locale).toFormat('HH:mm DD')}
                              unixTimestamp={interval.timestamp}
                            />,
                          }}
                        />
                        <br />
                        <FormattedMessage
                          id="trade.trade_list.trade_list_table.cell.timestamp.interval.content"
                          defaultMessage="{formattedInterval}"
                          values={{
                            formattedInterval: <small>{intervalLength}</small>,
                          }}
                        />
                      </div>
                    </th>
                    <td data-testid="trade-summary" style={{ whiteSpace: 'nowrap' }}>
                      {buildTradeSummary(trade)}
                    </td>
                    <td colSpan={2}>
                      <BadgeTradeDirection direction={direction} />
                      <br />
                      <BadgeTradeType type={type} />
                    </td>
                    <td>
                      <strong>
                        {meter.title}
                      </strong>
                      <br />
                      {meter.identifier}
                      {propertyReference && <br />}
                      {propertyReference}
                    </td>
                    <td>
                      {direction === TRADE_DIRECTION_BUY
                        ? getTrader(intl, seller)
                        : getTrader(intl, buyer)}
                    </td>
                    <td>
                      <UUID
                        copyTitle={copyTradeRuleIdTitle}
                        copySuccessMsg={copyTradeRuleIdSuccessMsg}
                        url={`/properties/${propertyId}/trade-rules/${ruleId}`}
                        uuid={ruleId}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-top-0">
              {(pageCount > 1) && (
              <tr>
                <PaginationTableCell colSpan={8}>
                  <IntraDayPagination>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={pageCount}
                      onPageChangeFunc={handlePageChange}
                      ariaLabel={intl.formatMessage({
                        id: 'trade.trade_list.trade_list_table.pagination.navigation.label',
                        defaultMessage: 'Trade pagination navigation',
                      })}
                    />
                    <div className="pagination-info">
                      <small className="text-muted">
                        {intl.formatMessage({
                          id: 'trade.trade_list.trade_list_table.pagination.info.label',
                          defaultMessage: 'Showing {indexStart}-{indexFinish} of {totalCount} trades',
                        }, {
                          indexStart: pageStartIndex + 1,
                          indexFinish: pageEndIndex,
                          totalCount: itemCount,
                        })}
                      </small>
                    </div>
                  </IntraDayPagination>
                </PaginationTableCell>
              </tr>
              )}
              <TradesSummary trades={trades} direction={TRADE_DIRECTION_BUY} />
              <TradesSummary trades={trades} direction={TRADE_DIRECTION_SELL} />
            </tfoot>
          </table>
        </div>
        <div className="card-footer">
          <div className="trade-history-pagination">
            <nav aria-label="Trade history navigation">
              <InterDayPagination className="pagination">
                <li className="page-item">
                  <Link
                    className="page-link trades-previous-day"
                    to={`/properties/${propertyId}/trades/history/?date=${before.toISODate()}`}
                    onClick={(event) => (setDate(event, before))}
                  >
                    {intl.formatMessage({ id: 'trade.trade_list.trade_list_table.footer.previous_day.label', defaultMessage: 'Previous day' })}
                  </Link>
                </li>
                <li className="page-item">
                  <Link
                    className="page-link trades-next-day"
                    to={`/properties/${propertyId}/trades/history/?date=${after.toISODate()}`}
                    onClick={(event) => (setDate(event, after))}
                  >
                    {intl.formatMessage({ id: 'trade.trade_list.trade_list_table.footer.next_day.label', defaultMessage: 'Next day' })}

                  </Link>
                </li>
              </InterDayPagination>
            </nav>
          </div>
        </div>
      </div>
      )}
    </>
  );
}

TradeListTable.propTypes = {
  date: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  dateUpdateFunc: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func,
  dateOutOfRange: PropTypes.bool.isRequired,
  noDataMessage: PropTypes.string,
  overlayVisible: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  loadingOverlayProps: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  paginationInfo: PropTypes.object.isRequired,
  propertyId: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  trades: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default TradeListTable;
